// Vendors have typed these into free-text fields with zero validation, so
// the same "website" field might hold a bare domain, a full URL, someone's
// Instagram link, an @handle, or literally "N/A". This module inspects the
// actual VALUE rather than trusting the field it came from, and only falls
// back to the field's name when the content itself gives no clue (a bare
// handle like "mama_njeri" typed into the "instagram" field).

const JUNK_VALUES = new Set(["n/a", "na", "none", "-", "--", "nil", "null", "undefined", ""]);

// Host → platform. Matches exact host or any subdomain of it, so
// "m.facebook.com" and "www.instagram.com" both resolve correctly.
const KNOWN_HOSTS = {
  instagram: ["instagram.com", "instagr.am"],
  facebook:  ["facebook.com", "fb.com", "m.facebook.com", "fb.watch"],
  tiktok:    ["tiktok.com"],
  twitter:   ["twitter.com", "x.com"],
  whatsapp:  ["wa.me", "whatsapp.com"],
  youtube:   ["youtube.com", "youtu.be"],
  linkedin:  ["linkedin.com"],
};

const stripTrailingSlash = (path) => path.replace(/\/+$/, "");

// `new URL()` throws on bare domains ("mamanjeri.com") with no scheme, so
// try with https:// prepended if the raw string doesn't already have one.
function tryParseUrl(value) {
  const candidates = /^https?:\/\//i.test(value) ? [value] : [`https://${value}`];
  for (const candidate of candidates) {
    try {
      return new URL(candidate);
    } catch {
      /* try next candidate */
    }
  }
  return null;
}

function detectHostPlatform(hostname) {
  const h = hostname.replace(/^www\./i, "").toLowerCase();
  for (const [platform, hosts] of Object.entries(KNOWN_HOSTS)) {
    if (hosts.some((known) => h === known || h.endsWith(`.${known}`))) return platform;
  }
  return null;
}

/**
 * Normalize one raw field value into { platform, url, label } or null.
 *
 * `fieldHint` ("website" | "instagram" | "facebook") is only used as a
 * fallback for bare handles with no domain in them at all — actual URL
 * content always wins over the field it was typed into.
 */
function parseSocialValue(raw, fieldHint) {
  if (raw == null) return null;
  const value = String(raw).trim();
  if (!value || JUNK_VALUES.has(value.toLowerCase())) return null;

  // Looks like it has a domain in it somewhere (either a full URL, or a
  // bare "something.tld" shape) — worth trying to parse as a URL.
  const looksUrlish =
    /^https?:\/\//i.test(value) || /^[\w-]+(\.[\w-]+)+(\/|$)/i.test(value.replace(/^@/, ""));

  if (looksUrlish) {
    const parsed = tryParseUrl(value.replace(/^@/, ""));
    if (parsed) {
      const cleanPath = stripTrailingSlash(parsed.pathname);
      const hostname = parsed.hostname.replace(/^www\./i, "");
      const detected = detectHostPlatform(parsed.hostname);

      if (detected) {
        return {
          platform: detected,
          url: `https://${hostname}${cleanPath}`,
          label: cleanPath.replace(/^\//, "") || hostname,
        };
      }

      // A real domain, just not a social platform we recognize — treat
      // as a generic website no matter which field it was typed into.
      return {
        platform: "website",
        url: `https://${hostname}${cleanPath}`,
        label: hostname + cleanPath,
      };
    }
  }

  // Not URL-shaped at all — a bare handle/username. Trust the field name
  // here since the content alone can't tell us the platform.
  const handle = value.replace(/^@/, "");
  if (!handle) return null;

  if (fieldHint === "instagram") {
    return { platform: "instagram", url: `https://instagram.com/${handle}`, label: `@${handle}` };
  }
  if (fieldHint === "facebook") {
    return { platform: "facebook", url: `https://facebook.com/${handle}`, label: handle };
  }
  // A "website" field with plain text that isn't a domain (e.g. "coming
  // soon") — nothing sane to link to. Surface it as text, not a dead link.
  return { platform: "website", url: null, label: value };
}

/**
 * Parse all three raw fields for a vendor and return a de-duplicated,
 * ordered list of social entries — de-duped by resolved URL so a link
 * pasted into the wrong field doesn't show up twice.
 */
function getVendorSocials({ website, instagram, facebook }) {
  const candidates = [
    parseSocialValue(website, "website"),
    parseSocialValue(instagram, "instagram"),
    parseSocialValue(facebook, "facebook"),
  ].filter(Boolean);

  const seen = new Set();
  const result = [];
  for (const c of candidates) {
    const key = c.url || `${c.platform}:${c.label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(c);
  }

  // Stable display order regardless of which field each was found in.
  const order = ["website", "instagram", "facebook", "tiktok", "twitter", "youtube", "linkedin", "whatsapp"];
  result.sort((a, b) => order.indexOf(a.platform) - order.indexOf(b.platform));
  return result;
}

module.exports = { parseSocialValue, getVendorSocials };